import { useQuery } from '@tanstack/react-query'
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons'
import { useCallback, useMemo, useState } from 'react'
import { Control, Controller, Path } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { Option } from '~/shared/config/constants'
import { filterOptions } from '~/shared/lib/filterOptions'
import { DrawerComponent, useDrawer } from '~/shared/ui/Drawer'
import { SkeletonList } from '~/shared/ui/SkeletonList'
import { RadioGroup } from './RadioGroup'
import {
  Title,
  DrawerWrapper,
  InputWrapper,
  InputIcon,
  VisibleInput,
  SearchInput,
  SearchTitle,
} from './styled'

type AsyncSelectProps<TFieldValues extends Record<string, Option>> = {
  name: Path<TFieldValues>
  control?: Control<TFieldValues>
  fetchOptions: (search: string) => Promise<Option[]>
  label: string
  modalTitle: string
  queryKey?: unknown[]
  canAsyncSearch?: boolean
  disabled?: boolean
  drawerFullScreen?: boolean
}

export function AsyncSelect<TFieldValues extends Record<string, Option>>({
  name,
  control,
  fetchOptions,
  label,
  modalTitle,
  queryKey,
  canAsyncSearch,
  disabled,
  drawerFullScreen = true,
}: AsyncSelectProps<TFieldValues>) {
  const [isShow, openDrawer, closeDrawer] = useDrawer()
  const [search, setSearch] = useState<string>('')

  const handleDrawerClose = () => {
    setSearch('')
    closeDrawer()
  }

  const [debouncedSearch] = useDebounce(canAsyncSearch ? search : '', 200)

  const { isFetching, data } = useQuery<Option[]>(
    ['async-select-options', name, debouncedSearch, ...(queryKey || [])],
    () => fetchOptions(debouncedSearch),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
    },
  )

  const options = useMemo(() => data ?? [], [data])

  const handleInputChange = useCallback(
    (value?: string | null) => {
      setSearch(value || '')
    },
    [setSearch],
  )

  const filteredOptions = useMemo(
    () => (canAsyncSearch ? options : filterOptions(search, options)),
    [canAsyncSearch, options, search],
  )

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={undefined}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => {
        return (
          <div>
            <InputWrapper onClick={disabled ? undefined : openDrawer}>
              <VisibleInput
                name={name}
                className={invalid ? 'ion-invalid ion-touched' : ''}
                disabled={disabled}
                value={value?.label}
                label={label}
                errorText={error?.message || 'Обязательное поле'}
                labelPlacement='floating'
                fill='outline'
                mode='md'
              />
              <InputIcon
                icon={isShow ? chevronUpOutline : chevronDownOutline}
              />
            </InputWrapper>

            <DrawerComponent
              isShow={isShow}
              onCancel={handleDrawerClose}
              contentProps={
                drawerFullScreen
                  ? {
                      className: 'ion-content-scroll-host',
                      style: { height: '90vh' },
                    }
                  : {}
              }
            >
              <DrawerWrapper>
                <Title>{modalTitle}</Title>

                <SearchInput
                  label='Поиск'
                  labelPlacement='floating'
                  fill='outline'
                  mode='md'
                  onIonInput={(e) => handleInputChange(e.detail.value)}
                />

                {isFetching && <SkeletonList count={10} skeletonHeight={56} />}

                {!isFetching && !options.length && (
                  <div>Ошибка получения данных</div>
                )}

                {!isFetching && !filteredOptions.length && (
                  <div>Ничего не найдено</div>
                )}

                {!isFetching && Boolean(filteredOptions?.length) && (
                  <>
                    {search && <SearchTitle>Результат</SearchTitle>}

                    <RadioGroup
                      data-testid='select-drawer-options'
                      options={filteredOptions}
                      value={value}
                      onChange={(option) => {
                        onChange(option)
                        handleDrawerClose()
                      }}
                    />
                  </>
                )}
              </DrawerWrapper>
            </DrawerComponent>
          </div>
        )
      }}
    />
  )
}
